"use strict";
/**
 * BEST2 String Operations
 *
 * This module implements string manipulation opcodes for the BEST2 interpreter:
 * - SCAT (string concatenate)
 * - SLEN (string length)
 * - SCMP (string compare)
 * - SGET/SSET (get/set character at index)
 * - STOI/ITOS (string to int / int to string)
 * - STOH/HTOS (string to hex / hex to string)
 * - SFIND (find substring)
 * - SSUB (substring)
 * - SUPPER/SLOWER (uppercase/lowercase)
 * - STRIM (trim whitespace)
 * - SREV (reverse string)
 * - SERASE (erase portion of string)
 * - SPASTE (paste string into another)
 * - STOKEN (extract token)
 *
 * All string operations work with S registers (S0-SF).
 * String length is limited by maxStringSize (default 255).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.dez2fix = exports.hex2fix = exports.fix2dez = exports.fix2hex = exports.a2fix = exports.strcat = exports.strlen = void 0;
exports.scat = scat;
exports.slen = slen;
exports.scmp = scmp;
exports.sget = sget;
exports.sset = sset;
exports.stoi = stoi;
exports.itos = itos;
exports.stoh = stoh;
exports.htos = htos;
exports.sfind = sfind;
exports.ssub = ssub;
exports.supper = supper;
exports.slower = slower;
exports.strim = strim;
exports.srev = srev;
exports.serase = serase;
exports.spaste = spaste;
exports.stoken = stoken;
exports.scopy = scopy;
exports.sclear = sclear;
exports.ssetImm = ssetImm;
exports.strcmp = strcmp;
exports.ufix2dez = ufix2dez;
exports.udez2fix = udez2fix;
const register_values_1 = require("./register-values");
/**
 * SCAT - String concatenate.
 *
 * Appends the source string to the destination string.
 * Result is truncated to maxStringSize if necessary.
 *
 * @param registers - Register set
 * @param destination - Destination S register (modified)
 * @param source - Source S register
 *
 * @example
 * ```ts
 * // S0 = "Hello", S1 = " World"
 * scat(registers, { kind: "S", index: 0 }, { kind: "S", index: 1 });
 * // S0 = "Hello World"
 * ```
 */
function scat(registers, destination, source) {
    const destValue = (0, register_values_1.getStringValue)(registers, destination);
    const srcValue = (0, register_values_1.getStringValue)(registers, source);
    (0, register_values_1.setStringValue)(registers, destination, destValue + srcValue);
}
/**
 * SLEN - String length.
 *
 * Gets the length of the string and stores it in the destination integer register.
 *
 * @param registers - Register set
 * @param destination - Destination integer register (receives length)
 * @param source - Source S register
 *
 * @example
 * ```ts
 * // S0 = "Hello"
 * slen(registers, { kind: "I", index: 0 }, { kind: "S", index: 0 });
 * // I0 = 5
 * ```
 */
function slen(registers, destination, source) {
    const value = (0, register_values_1.getStringValue)(registers, source);
    (0, register_values_1.setIntValue)(registers, destination, value.length);
}
/**
 * SCMP - String/byte-array compare (opcode 0x20).
 *
 * Mirrors C# `OpScmp`: byte-array equality on the raw `GetArrayData()`
 * payload, with only the Z flag updated (C/S/V preserved).
 *
 *     Zero = data1.Length == data2.Length && data1.SequenceEqual(data2);
 *
 * Note that this is NOT a lexicographic compare — there's no S/C flag
 * for less-than/greater-than. For the inverted-Z variant used by BEST2
 * `strcmp` see {@link strcmp}.
 *
 * @param registers - Register set
 * @param flags - CPU flags (only Z is modified)
 * @param left - First S register
 * @param right - Second S register
 */
function scmp(registers, flags, left, right) {
    const leftBytes = (0, register_values_1.getBinaryValue)(registers, left);
    const rightBytes = (0, register_values_1.getBinaryValue)(registers, right);
    if (leftBytes.length !== rightBytes.length) {
        flags.z = false;
        return;
    }
    for (let i = 0; i < leftBytes.length; i += 1) {
        if (leftBytes[i] !== rightBytes[i]) {
            flags.z = false;
            return;
        }
    }
    flags.z = true;
}
/**
 * SGET - Get character at index.
 *
 * Gets the character code (byte value) at the specified index.
 * If index is out of bounds, returns 0.
 *
 * @param registers - Register set
 * @param destination - Destination integer register (receives char code)
 * @param source - Source S register
 * @param indexReg - Index register (0-based position)
 *
 * @example
 * ```ts
 * // S0 = "Hello", I1 = 1
 * sget(registers, { kind: "B", index: 0 }, { kind: "S", index: 0 }, { kind: "I", index: 1 });
 * // B0 = 101 ('e')
 * ```
 */
function sget(registers, destination, source, indexReg) {
    const str = (0, register_values_1.getStringValue)(registers, source);
    const index = (0, register_values_1.getIntValue)(registers, indexReg);
    if (index < 0 || index >= str.length) {
        (0, register_values_1.setIntValue)(registers, destination, 0);
    }
    else {
        (0, register_values_1.setIntValue)(registers, destination, str.charCodeAt(index));
    }
}
/**
 * SSET - Set character at index.
 *
 * Sets the character at the specified index to the given character code.
 * If index is out of bounds or beyond string length, operation is ignored.
 *
 * @param registers - Register set
 * @param destination - Destination S register (modified)
 * @param indexReg - Index register (0-based position)
 * @param charReg - Character code register
 *
 * @example
 * ```ts
 * // S0 = "Hello", I0 = 0, B0 = 74 ('J')
 * sset(registers, { kind: "S", index: 0 }, { kind: "I", index: 0 }, { kind: "B", index: 0 });
 * // S0 = "Jello"
 * ```
 */
function sset(registers, destination, indexReg, charReg) {
    const str = (0, register_values_1.getStringValue)(registers, destination);
    const index = (0, register_values_1.getIntValue)(registers, indexReg);
    const charCode = (0, register_values_1.getIntValue)(registers, charReg);
    if (index >= 0 && index < str.length) {
        const newStr = str.substring(0, index) +
            String.fromCharCode(charCode & 0xff) +
            str.substring(index + 1);
        (0, register_values_1.setStringValue)(registers, destination, newStr);
    }
}
/**
 * STOI - String to integer.
 *
 * Parses the string as a decimal integer.
 * Sets Z flag if parsing failed.
 *
 * @param registers - Register set
 * @param flags - CPU flags (Z set on parse error)
 * @param destination - Destination integer register
 * @param source - Source S register
 *
 * @example
 * ```ts
 * // S0 = "12345"
 * stoi(registers, flags, { kind: "L", index: 0 }, { kind: "S", index: 0 });
 * // L0 = 12345, Z = false
 * ```
 */
function stoi(registers, flags, destination, source) {
    const str = (0, register_values_1.getStringValue)(registers, source).trim();
    const value = parseInt(str, 10);
    if (isNaN(value)) {
        (0, register_values_1.setIntValue)(registers, destination, 0);
        flags.z = true;
        flags.c = true;
    }
    else {
        (0, register_values_1.setIntValue)(registers, destination, value);
        flags.z = false;
        flags.c = false;
    }
    flags.v = false;
    flags.s = value < 0;
}
/**
 * ITOS - Integer to string.
 *
 * Converts an integer to its decimal string representation.
 *
 * @param registers - Register set
 * @param destination - Destination S register
 * @param source - Source integer register
 *
 * @example
 * ```ts
 * // L0 = 12345
 * itos(registers, { kind: "S", index: 0 }, { kind: "L", index: 0 });
 * // S0 = "12345"
 * ```
 */
function itos(registers, destination, source) {
    const value = (0, register_values_1.getIntValue)(registers, source);
    // For L registers, treat as signed 32-bit
    let signedValue = value;
    if (source.kind === "L") {
        signedValue = value | 0; // Convert to signed 32-bit
    }
    (0, register_values_1.setStringValue)(registers, destination, signedValue.toString(10));
}
/**
 * STOH - String to hex integer.
 *
 * Parses the string as a hexadecimal integer.
 * Handles optional "0x" or "$" prefix.
 *
 * @param registers - Register set
 * @param flags - CPU flags (Z set on parse error)
 * @param destination - Destination integer register
 * @param source - Source S register
 *
 * @example
 * ```ts
 * // S0 = "FF"
 * stoh(registers, flags, { kind: "L", index: 0 }, { kind: "S", index: 0 });
 * // L0 = 255
 * ```
 */
function stoh(registers, flags, destination, source) {
    let str = (0, register_values_1.getStringValue)(registers, source).trim();
    // Remove common hex prefixes
    if (str.startsWith("0x") || str.startsWith("0X")) {
        str = str.substring(2);
    }
    else if (str.startsWith("$")) {
        str = str.substring(1);
    }
    const value = parseInt(str, 16);
    if (isNaN(value)) {
        (0, register_values_1.setIntValue)(registers, destination, 0);
        flags.z = true;
        flags.c = true;
    }
    else {
        (0, register_values_1.setIntValue)(registers, destination, value);
        flags.z = false;
        flags.c = false;
    }
    flags.v = false;
    flags.s = false;
}
/**
 * HTOS - Hex integer to string.
 *
 * Converts an integer to its hexadecimal string representation (uppercase).
 *
 * @param registers - Register set
 * @param destination - Destination S register
 * @param source - Source integer register
 *
 * @example
 * ```ts
 * // L0 = 255
 * htos(registers, { kind: "S", index: 0 }, { kind: "L", index: 0 });
 * // S0 = "FF"
 * ```
 */
function htos(registers, destination, source) {
    const value = (0, register_values_1.getIntValue)(registers, source);
    // Convert to unsigned representation
    const unsigned = value >>> 0;
    (0, register_values_1.setStringValue)(registers, destination, unsigned.toString(16).toUpperCase());
}
/**
 * SFIND - Find substring.
 *
 * Finds the first occurrence of needle in haystack starting from startIndex.
 * Returns -1 if not found.
 *
 * @param registers - Register set
 * @param destination - Destination integer register (receives index or -1)
 * @param haystack - String to search in
 * @param needle - String to search for
 * @param startIndexReg - Start position for search (optional, defaults to 0)
 *
 * @example
 * ```ts
 * // S0 = "Hello World", S1 = "World"
 * sfind(registers, { kind: "I", index: 0 }, { kind: "S", index: 0 }, { kind: "S", index: 1 });
 * // I0 = 6
 * ```
 */
function sfind(registers, destination, haystack, needle, startIndexReg) {
    const haystackStr = (0, register_values_1.getStringValue)(registers, haystack);
    const needleStr = (0, register_values_1.getStringValue)(registers, needle);
    const startIndex = startIndexReg ? (0, register_values_1.getIntValue)(registers, startIndexReg) : 0;
    const index = haystackStr.indexOf(needleStr, startIndex);
    (0, register_values_1.setIntValue)(registers, destination, index);
}
/**
 * SSUB - Substring extraction.
 *
 * Extracts a substring from startIndex with the specified length.
 *
 * @param registers - Register set
 * @param destination - Destination S register
 * @param source - Source S register
 * @param startIndexReg - Start position
 * @param lengthReg - Length of substring
 *
 * @example
 * ```ts
 * // S0 = "Hello World", I0 = 6, I1 = 5
 * ssub(registers, { kind: "S", index: 1 }, { kind: "S", index: 0 },
 *      { kind: "I", index: 0 }, { kind: "I", index: 1 });
 * // S1 = "World"
 * ```
 */
function ssub(registers, destination, source, startIndexReg, lengthReg) {
    const str = (0, register_values_1.getStringValue)(registers, source);
    const startIndex = (0, register_values_1.getIntValue)(registers, startIndexReg);
    const length = (0, register_values_1.getIntValue)(registers, lengthReg);
    if (startIndex < 0 || startIndex >= str.length) {
        (0, register_values_1.setStringValue)(registers, destination, "");
    }
    else {
        (0, register_values_1.setStringValue)(registers, destination, str.substring(startIndex, startIndex + length));
    }
}
/**
 * SUPPER - Convert string to uppercase.
 *
 * @param registers - Register set
 * @param destination - S register to convert (modified in place)
 *
 * @example
 * ```ts
 * // S0 = "Hello World"
 * supper(registers, { kind: "S", index: 0 });
 * // S0 = "HELLO WORLD"
 * ```
 */
function supper(registers, destination) {
    const value = (0, register_values_1.getStringValue)(registers, destination);
    (0, register_values_1.setStringValue)(registers, destination, value.toUpperCase());
}
/**
 * SLOWER - Convert string to lowercase.
 *
 * @param registers - Register set
 * @param destination - S register to convert (modified in place)
 *
 * @example
 * ```ts
 * // S0 = "Hello World"
 * slower(registers, { kind: "S", index: 0 });
 * // S0 = "hello world"
 * ```
 */
function slower(registers, destination) {
    const value = (0, register_values_1.getStringValue)(registers, destination);
    (0, register_values_1.setStringValue)(registers, destination, value.toLowerCase());
}
/**
 * STRIM - Trim whitespace from both ends.
 *
 * @param registers - Register set
 * @param destination - S register to trim (modified in place)
 *
 * @example
 * ```ts
 * // S0 = "  Hello World  "
 * strim(registers, { kind: "S", index: 0 });
 * // S0 = "Hello World"
 * ```
 */
function strim(registers, destination) {
    const value = (0, register_values_1.getStringValue)(registers, destination);
    (0, register_values_1.setStringValue)(registers, destination, value.trim());
}
/**
 * SREV - Reverse string.
 *
 * @param registers - Register set
 * @param destination - S register to reverse (modified in place)
 *
 * @example
 * ```ts
 * // S0 = "Hello"
 * srev(registers, { kind: "S", index: 0 });
 * // S0 = "olleH"
 * ```
 */
function srev(registers, destination) {
    const value = (0, register_values_1.getStringValue)(registers, destination);
    (0, register_values_1.setStringValue)(registers, destination, value.split("").reverse().join(""));
}
/**
 * SERASE - Erase portion of string.
 *
 * Removes characters from startIndex for the specified length.
 *
 * @param registers - Register set
 * @param destination - S register to modify
 * @param startIndexReg - Start position
 * @param lengthReg - Number of characters to remove
 *
 * @example
 * ```ts
 * // S0 = "Hello World", I0 = 5, I1 = 6
 * serase(registers, { kind: "S", index: 0 }, { kind: "I", index: 0 }, { kind: "I", index: 1 });
 * // S0 = "Hello"
 * ```
 */
function serase(registers, destination, startIndexReg, lengthReg) {
    const str = (0, register_values_1.getStringValue)(registers, destination);
    const startIndex = (0, register_values_1.getIntValue)(registers, startIndexReg);
    const length = (0, register_values_1.getIntValue)(registers, lengthReg);
    if (startIndex < 0 || startIndex >= str.length) {
        // Nothing to erase
        return;
    }
    const newStr = str.substring(0, startIndex) + str.substring(startIndex + length);
    (0, register_values_1.setStringValue)(registers, destination, newStr);
}
/**
 * SPASTE - Paste string into another.
 *
 * Inserts the source string into the destination at the specified position.
 *
 * @param registers - Register set
 * @param destination - S register to modify
 * @param source - S register containing string to insert
 * @param positionReg - Insert position
 *
 * @example
 * ```ts
 * // S0 = "Hello", S1 = " World", I0 = 5
 * spaste(registers, { kind: "S", index: 0 }, { kind: "S", index: 1 }, { kind: "I", index: 0 });
 * // S0 = "Hello World"
 * ```
 */
function spaste(registers, destination, source, positionReg) {
    const destStr = (0, register_values_1.getStringValue)(registers, destination);
    const srcStr = (0, register_values_1.getStringValue)(registers, source);
    const position = (0, register_values_1.getIntValue)(registers, positionReg);
    let newStr;
    if (position <= 0) {
        newStr = srcStr + destStr;
    }
    else if (position >= destStr.length) {
        newStr = destStr + srcStr;
    }
    else {
        newStr = destStr.substring(0, position) + srcStr + destStr.substring(position);
    }
    (0, register_values_1.setStringValue)(registers, destination, newStr);
}
/**
 * STOKEN - Extract token from string.
 *
 * Extracts a token from the source string using a delimiter.
 * The token index specifies which token to extract (0-based).
 *
 * @param registers - Register set
 * @param destination - Destination S register (receives token)
 * @param source - Source S register
 * @param delimiter - S register containing delimiter character(s)
 * @param tokenIndexReg - Token index (0-based)
 *
 * @example
 * ```ts
 * // S0 = "one,two,three", S1 = ",", I0 = 1
 * stoken(registers, { kind: "S", index: 2 }, { kind: "S", index: 0 },
 *        { kind: "S", index: 1 }, { kind: "I", index: 0 });
 * // S2 = "two"
 * ```
 */
function stoken(registers, destination, source, delimiter, tokenIndexReg) {
    const str = (0, register_values_1.getStringValue)(registers, source);
    const delim = (0, register_values_1.getStringValue)(registers, delimiter);
    const tokenIndex = (0, register_values_1.getIntValue)(registers, tokenIndexReg);
    if (delim.length === 0) {
        // No delimiter, return entire string for index 0
        if (tokenIndex === 0) {
            (0, register_values_1.setStringValue)(registers, destination, str);
        }
        else {
            (0, register_values_1.setStringValue)(registers, destination, "");
        }
        return;
    }
    const tokens = str.split(delim);
    if (tokenIndex < 0 || tokenIndex >= tokens.length) {
        (0, register_values_1.setStringValue)(registers, destination, "");
    }
    else {
        (0, register_values_1.setStringValue)(registers, destination, tokens[tokenIndex]);
    }
}
/**
 * SCOPY - Copy string from source to destination.
 *
 * @param registers - Register set
 * @param destination - Destination S register
 * @param source - Source S register
 *
 * @example
 * ```ts
 * // S1 = "Hello"
 * scopy(registers, { kind: "S", index: 0 }, { kind: "S", index: 1 });
 * // S0 = "Hello"
 * ```
 */
function scopy(registers, destination, source) {
    const value = (0, register_values_1.getStringValue)(registers, source);
    (0, register_values_1.setStringValue)(registers, destination, value);
}
/**
 * SCLEAR - Clear string (set to empty).
 *
 * @param registers - Register set
 * @param destination - S register to clear
 *
 * @example
 * ```ts
 * // S0 = "Hello"
 * sclear(registers, { kind: "S", index: 0 });
 * // S0 = ""
 * ```
 */
function sclear(registers, destination) {
    (0, register_values_1.setStringValue)(registers, destination, "");
}
/**
 * SSET_IMM - Set string to immediate value.
 *
 * @param registers - Register set
 * @param destination - Destination S register
 * @param value - String value to set
 *
 * @example
 * ```ts
 * ssetImm(registers, { kind: "S", index: 0 }, "Hello");
 * // S0 = "Hello"
 * ```
 */
function ssetImm(registers, destination, value) {
    (0, register_values_1.setStringValue)(registers, destination, value);
}
/**
 * STRCMP - Compare strings (opcode 0x8F).
 *
 * **NOT an alias for `scmp`.** C# `OpStrcmp` uses
 * `String.Compare(...) != 0` for Z, which is the *inverse* of `OpScmp`
 * (where Z = strings are equal). Only the Z flag is touched — C, S, V
 * are preserved.
 *
 * - Z flag: set if strings DIFFER (matches C# `StringComparison.Ordinal`)
 *
 * @param registers - Register set
 * @param flags - CPU flags (only Z is modified)
 * @param left - First S register
 * @param right - Second S register
 */
function strcmp(registers, flags, left, right) {
    const leftValue = (0, register_values_1.getStringValue)(registers, left);
    const rightValue = (0, register_values_1.getStringValue)(registers, right);
    flags.z = leftValue !== rightValue;
}
/**
 * STRLEN - Get string length (alternative name for SLEN).
 * This is an alias for slen used in some BEST2 dialects.
 */
exports.strlen = slen;
/**
 * STRCAT - Concatenate strings (alternative name for SCAT).
 * This is an alias for scat used in some BEST2 dialects.
 */
exports.strcat = scat;
exports.a2fix = stoi;
exports.fix2hex = htos;
exports.fix2dez = itos;
function ufix2dez(registers, destination, source) {
    const value = (0, register_values_1.getIntValue)(registers, source) >>> 0;
    (0, register_values_1.setStringValue)(registers, destination, value.toString(10));
}
exports.hex2fix = stoh;
exports.dez2fix = stoi;
function udez2fix(registers, flags, destination, source) {
    const str = (0, register_values_1.getStringValue)(registers, source).trim();
    const value = parseInt(str, 10);
    if (isNaN(value)) {
        (0, register_values_1.setIntValue)(registers, destination, 0);
        flags.z = true;
        flags.c = true;
    }
    else {
        (0, register_values_1.setIntValue)(registers, destination, value >>> 0);
        flags.z = false;
        flags.c = false;
    }
    flags.v = false;
    flags.s = false;
}
//# sourceMappingURL=string.js.map